/**
 * WAX Module - Consolidated exports for WAX blockchain functionality
 *
 * This module provides a clean API for all WAX blockchain operations including:
 * - Client-side wallet operations (WAX Cloud Wallet, Anchor, Wombat)
 * - Server-side transaction signing (Wharfkit)
 * - Account creation and treasury management
 * - Configuration and utilities
 */

// =============================================================================
// Configuration Exports
// =============================================================================
export {
  getWaxConfig,
  getTreasuryConfig,
  isTreasuryConfigured,
  waxConfig,
} from "./config";

export type { WaxConfig, TreasuryConfig } from "./config";

// =============================================================================
// Client Types (from types.ts)
// =============================================================================
export type {
  WaxUserAccount,
  WaxLoginResult,
  CloudWalletOptions,
  WaxOAuthConfig,
  WalletType,
  WaxAccountCreationOptions,
  TransactionAction,
  TransactionResult as ClientTransactionResult,
} from "./types";

// =============================================================================
// Client-Side Services (browser)
// =============================================================================
export { WaxClientService } from "./client";
export { WaxAccountCreationPlugin } from "./account-creation-plugin";

// Client singleton instance
import { WaxClientService } from "./client";
export const waxClientService = new WaxClientService();

// Legacy compatibility exports (aliases for client service)
export const wharfkitWaxService = waxClientService;
export const clientWaxService = waxClientService;
export const waxServiceClient = waxClientService;

// =============================================================================
// Server-Side Services (Wharfkit)
// =============================================================================
export {
  WharfkitServerService,
  WharfkitRpcClient,
  getWharfkitServerService,
  getWharfkitRpcClient,
  wharfkitServerService,
  wharfkitRpcClient,
} from "./wharfkit-server";

export type {
  TransactionResult,
  RpcResult,
  WaxAccountInfo,
  WaxChainInfo,
} from "./wharfkit-server";

// =============================================================================
// Account Creation Services
// =============================================================================
export {
  WaxAccountCreator,
  waxAccountCreator,
} from "./account-creator";

export type {
  WaxAccountCreationRequest,
  WaxAccountCreationResult,
} from "./account-creator";

// =============================================================================
// Treasury Services
// =============================================================================
export {
  WaxTreasury,
  waxTreasury,
  getMinWaxBalance,
  getMinRamBytes,
} from "./treasury";

export type {
  TreasuryBalance,
  TreasuryResources,
  ThresholdCheck,
} from "./treasury";
